import React from 'react';
import {
  Layers,
  Package,
  Code,
  Database,
  Settings,
  Server,
  Shield,
  GitBranch,
  Workflow,
  CheckCircle
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const Backend = () => {
  // [Le reste des constantes reste identique]
  const architectureLayers = [
    {
      layer: "1. Domain Layer",
      description: "Cœur métier de l'application",
      components: [
        "Entités métier (sans dépendances)",
        "Value Objects",
        "Agrégats",
        "Interfaces des repositories",
        "Events du domaine"
      ],
      principles: [
        "Immutabilité des entités",
        "Encapsulation stricte",
        "Logique métier centralisée"
      ]
    },
    {
      layer: "2. Application Layer",
      description: "Orchestration des cas d'utilisation",
      components: [
        "Use Cases / Services applicatifs",
        "DTOs",
        "Interfaces des services",
        "Event handlers",
        "Command/Query handlers"
      ],
      principles: [
        "CQRS si pertinent",
        "Validation des entrées",
        "Gestion des transactions"
      ]
    },
    {
      layer: "3. Infrastructure Layer",
      description: "Implémentations techniques",
      components: [
        "Implémentations des repositories",
        "Configurations ORM",
        "Services externes",
        "Messaging",
        "Caching"
      ],
      principles: [
        "Inversion de dépendance",
        "Adaptateurs découplés",
        "Configuration externalisée"
      ]
    },
    {
      layer: "4. Presentation Layer",
      description: "Interface avec l'extérieur",
      components: [
        "Controllers API",
        "Middlewares",
        "Serialization",
        "Documentation API",
        "Gestion des erreurs"
      ],
      principles: [
        "API RESTful",
        "Versioning d'API",
        "Sécurité des endpoints"
      ]
    }
  ];

  const projectStructure = [
    {
      directory: "src/Domain/",
      contents: [
        "Entities/",
        "ValueObjects/",
        "Events/",
        "Repositories/",
        "Services/",
        "Exceptions/"
      ]
    },
    {
      directory: "src/Application/",
      contents: [
        "UseCases/",
        "DTOs/",
        "Interfaces/",
        "EventHandlers/",
        "Validators/"
      ]
    },
    {
      directory: "src/Infrastructure/",
      contents: [
        "Persistence/",
        "ExternalServices/",
        "Messaging/",
        "Cache/",
        "Logging/"
      ]
    },
    {
      directory: "src/Presentation/",
      contents: [
        "Controllers/",
        "Middlewares/",
        "ViewModels/",
        "Documentation/"
      ]
    }
  ];

  const buildSteps = [
    {
      phase: "1. Setup Initial",
      steps: [
        "Structure des dossiers selon les couches",
        "Configuration de base (DI, logging)",
        "Setup des tests unitaires",
        "Configuration du linting et formatage"
      ]
    },
    {
      phase: "2. Développement Domain",
      steps: [
        "Modélisation des entités et value objects",
        "Définition des interfaces repository",
        "Implémentation des règles métier",
        "Tests unitaires du domaine"
      ]
    },
    {
      phase: "3. Implémentation Application",
      steps: [
        "Création des use cases",
        "Mapping des DTOs",
        "Configuration CQRS si nécessaire",
        "Tests d'intégration"
      ]
    },
    {
      phase: "4. Infrastructure",
      steps: [
        "Implémentation des repositories",
        "Setup base de données",
        "Configuration caching",
        "Intégration services externes"
      ]
    },
    {
      phase: "5. API et Documentation",
      steps: [
        "Développement des controllers",
        "Documentation OpenAPI",
        "Tests API end-to-end",
        "Sécurisation des endpoints"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "Architecture",
      practices: [
        "Respect strict des dépendances",
        "Injection de dépendances",
        "SOLID principles",
        "Fail-fast principle"
      ]
    },
    {
      category: "Code",
      practices: [
        "Code review systématique",
        "Tests unitaires exhaustifs",
        "Documentation des APIs",
        "Clean code principles"
      ]
    },
    {
      category: "Performance",
      practices: [
        "Optimisation des requêtes",
        "Caching stratégique",
        "Monitoring des performances",
        "Scalabilité horizontale"
      ]
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <h2 className="text-2xl font-bold text-center text-white">
            Architecture Backend
          </h2>
          <p className="text-center text-white mt-2 opacity-90">
            Guide de construction et bonnes pratiques
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Architecture Layers */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Layers className="mr-2" />
              Couches Architecturales
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {architectureLayers.map((layer, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-600 mb-2">{layer.layer}</h4>
                  <p className="text-sm text-gray-600 mb-3">{layer.description}</p>
                  
                  <div className="mb-3">
                    <h5 className="text-sm font-medium mb-2">Composants:</h5>
                    <ul className="space-y-1">
                      {layer.components.map((component, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <Package className="w-4 h-4 mr-2 mt-1 text-blue-500" />
                          {component}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-sm font-medium mb-2">Principes:</h5>
                    <ul className="space-y-1">
                      {layer.principles.map((principle, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 mt-1 text-green-500" />
                          {principle}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Structure */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <GitBranch className="mr-2" />
              Structure du Projet
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {projectStructure.map((dir, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{dir.directory}</h4>
                  <ul className="space-y-2">
                    {dir.contents.map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Code className="w-4 h-4 mr-2 text-blue-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Build Steps */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Workflow className="mr-2" />
              Étapes de Construction
            </h3>
            <div className="space-y-4">
              {buildSteps.map((phase, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm mr-3">
                      {index + 1}
                    </div>
                    <h4 className="font-medium">{phase.phase}</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-11">
                    {phase.steps.map((step, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {step}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Best Practices */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Shield className="mr-2" />
              Meilleures Pratiques
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bestPractices.map((category, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{category.category}</h4>
                  <ul className="space-y-2">
                    {category.practices.map((practice, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 mt-1 text-green-500" />
                        {practice}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Technical Stack */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-700 mb-3">
              Considérations Techniques
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Performance</h4>
                <ul className="space-y-1">
                  <li className="flex items-center text-sm text-gray-700">
                    <Server className="w-4 h-4 mr-2 text-blue-500" />
                    Architecture asynchrone quand pertinent
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <Database className="w-4 h-4 mr-2 text-blue-500" />
                    Optimisation des requêtes et indexes
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Sécurité</h4>
                <ul className="space-y-1">
                  <li className="flex items-center text-sm text-gray-700">
                    <Shield className="w-4 h-4 mr-2 text-blue-500" />
                    Validation des entrées stricte
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <Settings className="w-4 h-4 mr-2 text-blue-500" />
                    Gestion sécurisée des configurations
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Backend;